import React from 'react';
import styled, { useTheme } from 'styled-components';
import day from 'dayjs';
import { H3, LabelUppercase, P } from '../elements/Text';
import Button from '../elements/Button';
import { CompanyResponse, LEARNER_STATUS } from '../../api/admin';

import { prop } from 'styled-tools';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import {
  TableHead,
  TableRow,
  TableHeader,
  Table,
  TableWrapper,
  TableCell,
  TableBody,
  DateTableCellContent,
} from '../../components/Table';
import { SmallBadge } from '../../components/elements/Badge';
import { TOPIC_TO_COLOR } from '../../theme';
import { SmallTooltip } from '../../components/Tooltip';
import Checkbox from '../../components/elements/Checkbox';
import AssignLearnersToMentorModal from './Modals/AssignLearnersToMentorModal';
import dayjs from 'dayjs';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;
const CohortWrapper = styled.div`
  padding: 20px;
  min-width: max(800px, 92vw);
  border: 1px solid ${prop('theme.colors.bgActive')};
  background-color: ${prop('theme.colors.transparentBg')};
  border-radius: 8px;
  /* height: 90vh; */
  overflow-y: auto;

  table {
    margin-top: 12px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;

type Cohort = CompanyResponse['cohorts'][number];

export default function Cohort({
  cohort,
  companyId,
}: {
  cohort: Cohort;
  companyId: number;
}) {
  const humanReadableTimeSince = day
    .utc(cohort.startDate)
    .fromNow()
    .replace(/^\w/, (c) => c.toUpperCase());

  // end date doesn't store hour info (e.g. it will point to sunday 00:00:00)
  // so we set it as end of day + 1 second to get the correct duration
  const humanReadableDuration = day
    .utc(cohort.endDate)
    .endOf('day')
    .add(1, 'second')
    .diff(day.utc(cohort.startDate).startOf('day'), 'weeks');

  const [tableSelection, setTableSelection] = React.useState<
    Record<string, boolean>
  >({});

  const numSelected = Object.keys(tableSelection)
    .map((key) => tableSelection[key])
    .filter(Boolean).length;

  const [isAssignMentorsModalOpen, setIsAssignMentorsModalOpen] =
    React.useState(false);

  const invites = cohort.learners.filter(
    (learner) =>
      learner.status === LEARNER_STATUS.INVITE_PENDING ||
      learner.status === LEARNER_STATUS.INVITE_EXPIRED
  );
  const onboardedLearners = cohort.learners.filter(
    (learner) =>
      learner.status === LEARNER_STATUS.ONBOARDED ||
      learner.status === LEARNER_STATUS.RESTARTED
  );
  const selectedLearners = onboardedLearners.filter((learner) =>
    Object.keys(tableSelection).includes(learner.sessionId?.toString() ?? '')
  );

  const canAssignMentors =
    numSelected === 1 && dayjs.utc(cohort.endDate).isAfter(dayjs.utc());
  const assignMentorButtonTooltip =
    numSelected <= 0
      ? 'No learners selected'
      : numSelected > 1
      ? 'Can only assign mentor to one learner at a time'
      : dayjs.utc(cohort.endDate).isBefore(dayjs.utc())
      ? 'Cohort is in the past'
      : '';

  return (
    <CohortWrapper>
      <H3 style={{ marginTop: 16 }}>
        {day.utc(cohort.startDate).format('DD MMMM YYYY')} to{' '}
        {day.utc(cohort.endDate).format('DD MMMM YYYY')}
      </H3>
      <LabelUppercase style={{ marginTop: 12 }}>
        {humanReadableTimeSince}. Duration: {humanReadableDuration} weeks
      </LabelUppercase>
      {invites.length > 0 ? (
        <Row>
          <Column>
            <P>There are {invites.length} invites in this cohort.</P>
            <InvitesTable invites={invites} />
          </Column>
        </Row>
      ) : (
        <P>No invites in this cohort</P>
      )}
      {onboardedLearners.length > 0 ? (
        <Row>
          <Column>
            <Row>
              <Button
                label="Assign Mentors"
                type="secondary"
                faIcon="fa-solid fa-user-pen"
                showIconBeforeLabel
                tooltip={assignMentorButtonTooltip}
                onClick={() => setIsAssignMentorsModalOpen(true)}
                isDisabled={!canAssignMentors}
              />
            </Row>
            <P>
              There are {onboardedLearners.length} learners in this cohort.
              {numSelected > 0 && <> {numSelected} learners selected.</>}
            </P>
            <CohortLearnersTable
              cohortId={cohort.cycleId}
              learners={onboardedLearners}
              learnerSelection={tableSelection}
              setLearnerSelection={setTableSelection}
            />
          </Column>
        </Row>
      ) : (
        <P>No onboarded learners in this cohort</P>
      )}

      <AssignLearnersToMentorModal
        isOpen={isAssignMentorsModalOpen}
        onClose={() => setIsAssignMentorsModalOpen(false)}
        companyId={companyId}
        selectedSessionIds={selectedLearners.map(
          (learner) => learner.sessionId ?? -1
        )}
        cycleId={cohort.cycleId}
      />
    </CohortWrapper>
  );
}

const columnHelper = createColumnHelper<Cohort['learners'][number]>();

const StatusCell = (props: {
  getValue: () => string;
  row: {
    original: {
      status: string;
      invitedAt: Date;
      onboardedOrRestartedAt: Date | null;
      sessionId: number | null;
    };
  };
}) => {
  const theme = useTheme();
  const status = props.row.original.status;
  const invitedAt = props.row.original?.invitedAt;
  const onboardedAt = props.row.original?.onboardedOrRestartedAt;

  const tooltipText = `
    ${
      invitedAt
        ? `Invited at ${dayjs.utc(invitedAt).format('YYYY-MM-DD HH:mm')} UTC`
        : ''
    }
    ${
      onboardedAt
        ? `Onboarded (or restarted) at ${dayjs
            .utc(onboardedAt)
            .format('YYYY-MM-DD HH:mm')} UTC`
        : ''
    }
  `;
  const sessionId = props.row.original.sessionId;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <SmallBadge
        className={`status-${sessionId}`}
        label={status}
        isPrimary={status === 'onboarded'}
        colors={{
          backgroundColor: theme.colors.bgActive,
          textColor: theme.colors.textActive,
        }}
      />
      <SmallTooltip anchorSelect={`.status-${sessionId}`}>
        {tooltipText}
      </SmallTooltip>
    </div>
  );
};

const ModuleCell = (props: {
  getValue: () => string;
  row: { original: { module: { topic: string } } };
}) => {
  const theme = useTheme();
  const moduleTitle = props.getValue();
  const moduleTopic = props.row.original.module.topic;

  return (
    <SmallBadge
      label={moduleTitle}
      isPrimary
      colors={{
        backgroundColor:
          TOPIC_TO_COLOR[moduleTopic as keyof typeof TOPIC_TO_COLOR],
        textColor: theme.colors.textActive,
      }}
    />
  );
};

const MentorCell = (props: {
  getValue: () => null | {
    name: string;
    email: string;
    assignmentId: number;
  };
}) => {
  const mentor = props.getValue();
  if (!mentor) {
    return '-';
  }
  const mentorName = mentor.name;
  const mentorEmail = mentor.email;
  const mentorAssignmentId = mentor.assignmentId;
  return (
    <>
      <div className={`mentor-${mentorAssignmentId}`}>{mentorName}</div>
      <SmallTooltip anchorSelect={`.mentor-${mentorAssignmentId}`}>
        {mentorEmail}
      </SmallTooltip>
    </>
  );
};

const EmailButton = styled.div`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textActive};
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const learnersColumns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        isChecked={table.getIsAllRowsSelected()}
        isIndeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        isChecked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    meta: {
      isCentered: true,
      styles: {
        width: '50px',
      },
    },
  }),
  columnHelper.display({
    id: 'id',
    header: 'ID',
    cell: ({ row }) => {
      return row.original.sessionId;
    },
    meta: {
      styles: {
        width: '50px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <>
          <div className={`learner-${row.original.learnerId}`}>
            {row.original.name ?? 'N/A'}
          </div>
          <SmallTooltip anchorSelect={`.learner-${row.original.learnerId}`}>
            {row.original.email}
          </SmallTooltip>
        </>
      );
    },
    meta: { styles: { width: '200px', minWidth: '200px' } },
  }),
  columnHelper.accessor('module.contentTitle', {
    header: 'Module',
    cell: ModuleCell,
    meta: {
      styles: { width: '220px', minWidth: '220px' },
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: StatusCell,
    meta: {
      styles: {
        width: '160px',
        minWidth: '160px',
      },
    },
  }),
  columnHelper.accessor('mentor', {
    id: 'mentor',
    header: 'Mentor',
    cell: MentorCell,
    meta: {
      styles: {
        width: '200px',
        minWidth: '200px',
      },
    },
  }),
  columnHelper.display({
    id: 'core-progress',
    header: 'Core ',
    cell: ({ row }) => {
      const status = row.original.status;
      if (
        [LEARNER_STATUS.INVITE_PENDING, LEARNER_STATUS.INVITE_EXPIRED].includes(
          status
        )
      ) {
        return '-';
      }
      return (
        <>
          {row.original.module.coreSubmittedLessonsCount} /{' '}
          {row.original.module.coreLessonsCount}
        </>
      );
    },
    meta: {
      styles: {
        minWidth: '80px',
      },
    },
  }),
  columnHelper.display({
    id: 'stretch-progress',
    header: 'Stretch ',
    cell: ({ row }) => {
      const status = row.original.status;
      if (
        [LEARNER_STATUS.INVITE_PENDING, LEARNER_STATUS.INVITE_EXPIRED].includes(
          status
        )
      ) {
        return '-';
      }
      return (
        <>
          {row.original.module.stretchSubmittedLessonsCount} /{' '}
          {row.original.module.stretchLessonsCount}
        </>
      );
    },
    meta: {
      styles: {
        minWidth: '80px',
      },
    },
  }),
  columnHelper.accessor('module.aiMessageCount', {
    header: 'AI Messages',
    meta: {
      styles: {
        minWidth: '140px',
      },
    },
    cell: ({ row }) => {
      const status = row.original.status;
      if (
        [LEARNER_STATUS.INVITE_PENDING, LEARNER_STATUS.INVITE_EXPIRED].includes(
          status
        )
      ) {
        return '-';
      }
      return row.original.module.aiMessageCount;
    },
  }),
  columnHelper.accessor('lastActiveAt', {
    header: 'Last Active At',
    cell: (props) => <DateTableCellContent date={props.getValue()} />,
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
];

const Wrapper = styled(TableWrapper)`
  overflow-x: auto;
  table {
    min-width: 1200px;
  }
`;

const CohortLearnersTable = ({
  learners,
  learnerSelection,
  setLearnerSelection,
}: // cohortId,
{
  learners: Cohort['learners'];
  learnerSelection: Record<string, boolean>;
  setLearnerSelection: OnChangeFn<RowSelectionState>;
  cohortId: number;
}) => {
  const table = useReactTable({
    data: learners,
    columns: learnersColumns,
    state: {
      rowSelection: learnerSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setLearnerSelection,
    getRowId: (row) => row.sessionId?.toString() ?? '',
  });

  return (
    <Wrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} meta={header.column.columnDef.meta}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} isSelected={row.getIsSelected()}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} meta={cell.column.columnDef.meta}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Wrapper>
  );
};

const invitesColumns = [
  columnHelper.display({
    id: 'id',
    header: 'ID',
    cell: ({ row }) => {
      return row.original.inviteId;
    },
    meta: {
      styles: {
        width: '100px',
        minWidth: '100px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <>
          <div className={`learner-${row.original.learnerId}`}>
            {row.original.name ?? 'N/A'}
          </div>
          <SmallTooltip anchorSelect={`.learner-${row.original.learnerId}`}>
            {row.original.email}
          </SmallTooltip>
        </>
      );
    },
    meta: { styles: { width: '200px', minWidth: '200px' } },
  }),
  columnHelper.accessor('module.contentTitle', {
    header: 'Module',
    cell: ModuleCell,
    meta: {
      styles: { width: '220px', minWidth: '220px' },
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: StatusCell,
    meta: {
      styles: {
        width: '160px',
        minWidth: '160px',
      },
    },
  }),
  columnHelper.accessor('invitedAt', {
    header: 'Invited At',
    cell: (props) => <DateTableCellContent date={props.getValue()} />,
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const id = `email-${row.original.sessionId || row.original.inviteId}`;
      const trackId = row.original.module.trackId;
      const inviteId = row.original.inviteId;

      const onClick = () => {
        window.open(
          `https://enki.tryretool.com/apps/2d89b82e-77bb-11ec-b3b8-43fc08ef0f09/ops/learner-onboarding-emails#cohort=${trackId}&onboardinglearner=${inviteId}`,
          '_blank'
        );
      };
      return (
        <>
          <EmailButton className={id} onClick={onClick}>
            <i className="fa-solid fa-inboxes"></i>
          </EmailButton>
          <SmallTooltip anchorSelect={`.${id}`}>
            Open onboarding emails
          </SmallTooltip>
        </>
      );
    },
    meta: {
      styles: {
        width: '30px',
      },
    },
  }),
];

const InvitesTable = ({ invites }: { invites: Cohort['learners'] }) => {
  const table = useReactTable({
    data: invites,
    columns: invitesColumns,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <Wrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} meta={header.column.columnDef.meta}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} isSelected={row.getIsSelected()}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} meta={cell.column.columnDef.meta}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Wrapper>
  );
};
